import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Tooltip, Chip, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { Icon, Translate, RatingIcons } from '@hydra/common/components';
import DialogOutdatedCrits from '@hydra/common/components/ReviewDashboard/DialogOutdatedCrits';
import { useReviewContext, usePdfTemplateContext } from '@hydra/common/context';

import { getLightColor } from '@hydra/utils/colors';
import { getStatusObject } from '@hydra/utils/criterium';

const useStyles = makeStyles(theme => ({
  head: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(1)
  },
  importanceIcons: {
    display: 'inline-block',
    marginRight: theme.spacing(2)
  },
  solvabilityIcons: {
    display: 'inline-block'
  },
  statusIcon: {
    display: 'flex',
    marginRight: theme.spacing(0.75),
    marginTop: theme.spacing(0.375)
  },
  titleModern: {
    display: 'flex',
    lineHeight: '1.5rem',
    fontSize: theme.typography.fontSizeLarge
  },
  titleClassic: {
    display: 'flex',
    lineHeight: 1.6,
    fontSize: theme.typography.fontSizeMedium
  },
  icons: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  iconsModern: {
    marginTop: theme.spacing(1),
    marginLeft: 0
  },
  iconsClassic: {
    marginLeft: theme.spacing(3)
  },
  tooltip: {
    fontSize: theme.typography.fontSizeSmall,
    lineHeight: 1.25
  },
  outdatedChipWrapper: {
    marginLeft: theme.spacing(3)
  },
  chipRoot: {
    height: theme.spacing(2.25)
  },
  chipIcon: {
    color: theme.palette.grey[400],
    width: 10,
    height: 10,
    marginLeft: theme.spacing(0.5)
  },
  chipLabel: {
    color: theme.palette.grey[400],
    fontSize: theme.typography.fontSizeTiny,
    paddingRight: theme.spacing(0.5)
  }
}));

export const CriteriumHead = ({ criterium, iconsFillColor }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { isPdf, isLeadgen } = useReviewContext();
  const { pdfTheme } = usePdfTemplateContext();
  const { type, importance, solvability, status } = criterium;

  let emptyColor;
  if (isPdf) {
    emptyColor = getLightColor(iconsFillColor, 0.2);
  } else {
    emptyColor = theme.palette.grey[300];
  }

  const isModern = isPdf && pdfTheme === 'modern';

  const { text: statusText, type: statusType } = getStatusObject(status);
  const iconsClass = classnames(
    classes.icons,
    isModern ? classes.iconsModern : classes.iconsClassic
  );
  const iconSize = isModern ? 'base' : 'sm';
  const titleClass = isModern
    ? classes.titleModern
    : classes.titleClassic;
  const ratingIconSize = isModern ? 'sm' : 'xs';

  const [open, setOpen] = useState(false);
  const handleOpen = (event) => {
    event.stopPropagation();
    setOpen(true);
  };
  const handleClose = (event) => {
    event.stopPropagation();
    setOpen(false);
  };

  return (
    <div className='mex-criterium-head'>
      <Typography variant='h3' className={classes.head}>
        <Tooltip title={<Translate content={`${statusText}`} className={classes.tooltip} />} placement='top'>
          <span className={classnames(classes.statusIcon, `nr-review-${type}-score-icon`)}>
            <Icon type={statusType} size={iconSize} />
          </span>
        </Tooltip>
        <span className={titleClass}>
          <Translate content={type} />
        </span>
      </Typography>
      <div className={iconsClass}>
        {
          importance > 0 &&
            <Tooltip
              title={<Translate content={`criterion_importance${importance}`} className={classes.tooltip} />}
              placement='top'
            >
              <div className={classnames(classes.importanceIcons, `nr-review-${type}-impact-icons`)}>
                <RatingIcons
                  rating={importance}
                  type='importance'
                  fillColor={iconsFillColor}
                  emptyColor={emptyColor}
                  size={ratingIconSize}
                />
              </div>
            </Tooltip>
        }
        {
          solvability > 0 &&
            <Tooltip title={<Translate content={`criterion_solvability${solvability}`} className={classes.tooltip} />} placement='top'>
              <div className={classnames(classes.solvabilityIcons, `nr-review-${type}-effort-icons`)}>
                <RatingIcons
                  rating={solvability}
                  type='solvability'
                  fillColor={iconsFillColor}
                  emptyColor={emptyColor}
                  size={ratingIconSize}
                />
              </div>
            </Tooltip>
        }
      </div>
      {
        criterium.isOutdated && !isPdf && !isLeadgen &&
          <div className={classes.outdatedChipWrapper}>
            <Chip
              onClick={handleOpen}
              variant='outlined'
              size='small'
              icon={<Icon type='outdated' />}
              label={<Translate content='review_outdated_criteria_outdated' />}
              classes={{
                root: classes.chipRoot,
                icon: classes.chipIcon,
                label: classes.chipLabel
              }}
            />
          </div>
      }
      <DialogOutdatedCrits open={open} handleClose={handleClose} />
    </div>
  );
};

CriteriumHead.propTypes = {
  criterium: PropTypes.object.isRequired
};

export default CriteriumHead;
